import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { alpha, Palette, pick } from './helpers';
import { useServicePalette } from './palette';

type Span = [string, number];
type TermLine = Span[];

const FG = -1;
const DIM = -2;
const BRIGHT = -3;

const spanColour = (palette: Palette, tint: number) =>
    tint === FG
        ? alpha(palette.foreground, 0.78)
        : tint === DIM
        ? alpha(palette.foreground, 0.38)
        : tint === BRIGHT
        ? alpha(palette.foreground, 0.98)
        : alpha(pick(palette, tint), 0.95);

const Frame = styled.div<{ $palette: Palette }>`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: hidden;
    background: ${({ $palette }) => $palette.background};
`;

const ChromeBar = styled.div<{ $palette: Palette }>`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 1.9em;
    background: ${({ $palette }) => alpha($palette.foreground, 0.07)};
`;

const Dots = styled.div`
    position: absolute;
    left: 0.85em;
    display: flex;
    gap: 0.4em;
`;

const Dot = styled.span<{ $colour: string }>`
    width: 0.55em;
    height: 0.55em;
    border-radius: 50%;
    background: ${({ $colour }) => $colour};
`;

const Title = styled.span<{ $palette: Palette }>`
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68em;
    color: ${({ $palette }) => alpha($palette.foreground, 0.45)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Body = styled.pre`
    flex: 1;
    min-height: 0;
    margin: 0;
    overflow: hidden;
    padding: 0.5em 0.7em;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68em;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    user-select: text;
    cursor: text;
`;

const Row = styled.div`
    min-height: 1em;
`;

type Task = { name: string; items?: string[]; handler?: string; retries?: boolean; include?: string; diff?: [string, string] };
type Play = { name: string; hosts: string[]; tasks: Task[]; handlers: string[] };

const SITE: Play[] = [
    {
        name: 'common', hosts: ['web-01', 'web-02', 'web-03', 'app-01', 'app-02', 'db-01', 'db-02', 'cache-01', 'mon-01'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'common : Set hostname' }, { name: 'common : Configure timezone', diff: ['-Etc/UTC', '+Europe/London'] },
            { name: 'common : Install baseline packages', items: ['curl', 'jq', 'htop', 'unzip', 'chrony'] }, { name: 'common : Ensure chrony running' },
            { name: 'common : Create service users', include: 'roles/common/tasks/users.yml', items: ['deploy', 'app', 'backup'] },
            { name: 'common : Deploy sshd_config', handler: 'restart sshd', diff: ['-PermitRootLogin yes', '+PermitRootLogin no'] }, { name: 'common : Configure unattended-upgrades' },
            { name: 'common : Set sysctl tuning', items: ['net.core.somaxconn=4096', 'vm.swappiness=10', 'fs.file-max=2097152'] }, { name: 'common : Install node_exporter' },
            { name: 'common : Deploy promtail config', handler: 'restart promtail' }, { name: 'common : Harden journald retention' },
        ],
        handlers: ['restart sshd', 'restart promtail'],
    },
    {
        name: 'web', hosts: ['web-01', 'web-02', 'web-03'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'nginx : Install nginx' }, { name: 'nginx : Remove default vhost' },
            { name: 'nginx : Template vhosts', items: ['api.example.com', 'www.example.com', 'assets.example.com'], handler: 'reload nginx' },
            { name: 'nginx : Deploy tls certificates', handler: 'reload nginx' }, { name: 'nginx : Configure rate limiting zone' },
            { name: 'nginx : Enable gzip and brotli', diff: ['-gzip off;', '+gzip on;'] }, { name: 'nginx : Validate config (nginx -t)' },
            { name: 'nginx : Ensure nginx enabled and running' }, { name: 'firewall : Allow 80/443', items: ['80/tcp', '443/tcp'] },
        ],
        handlers: ['reload nginx'],
    },
    {
        name: 'app', hosts: ['app-01', 'app-02'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'app : Install runtime 22.x' }, { name: 'app : Create release directory' },
            { name: 'app : Fetch artefact atlas-api-1.9.3.tgz' }, { name: 'app : Unpack release' }, { name: 'app : Install dependencies (npm ci)' },
            { name: 'app : Template environment file', diff: ['-REPLICAS=4', '+REPLICAS=6'] }, { name: 'app : Run database migrations' },
            { name: 'app : Symlink current release', handler: 'restart atlas-api' }, { name: 'app : Wait for health endpoint', retries: true },
            { name: 'app : Prune old releases (keep 5)' },
        ],
        handlers: ['restart atlas-api'],
    },
    {
        name: 'db', hosts: ['db-01', 'db-02'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'postgres : Install postgresql-16' }, { name: 'postgres : Initialise cluster' },
            { name: 'postgres : Template postgresql.conf', diff: ['-shared_buffers = 128MB', '+shared_buffers = 8GB'], handler: 'restart postgresql' },
            { name: 'postgres : Template pg_hba.conf', handler: 'reload postgresql' }, { name: 'postgres : Create replication role' },
            { name: 'postgres : Configure streaming replica' }, { name: 'postgres : Wait for port 5432', retries: true },
            { name: 'postgres : Create application databases', items: ['orders', 'billing', 'ledger'] }, { name: 'postgres : Schedule pg_dump backups' },
        ],
        handlers: ['restart postgresql', 'reload postgresql'],
    },
    {
        name: 'cache', hosts: ['cache-01'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'redis : Install redis-server' }, { name: 'redis : Set maxmemory 4gb', handler: 'restart redis' },
            { name: 'redis : Set eviction policy allkeys-lru' }, { name: 'redis : Enable AOF persistence' }, { name: 'redis : Require auth' }, { name: 'redis : Ensure redis running' },
        ],
        handlers: ['restart redis'],
    },
    {
        name: 'monitoring', hosts: ['mon-01'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'prometheus : Install prometheus' }, { name: 'prometheus : Template scrape config', handler: 'reload prometheus' },
            { name: 'prometheus : Register targets', items: ['web-01:9100', 'web-02:9100', 'web-03:9100', 'app-01:9100', 'app-02:9100', 'db-01:9100', 'db-02:9100', 'cache-01:9100'] },
            { name: 'prometheus : Deploy alert rules', items: ['HighErrorRate', 'ReplicationLag', 'DiskWillFill', 'CertExpiring'] }, { name: 'grafana : Install grafana' },
            { name: 'grafana : Provision dashboards', items: ['api-overview', 'postgres', 'nginx', 'node'] }, { name: 'alertmanager : Configure pagerduty route' },
        ],
        handlers: ['reload prometheus'],
    },
    {
        name: 'backup', hosts: ['db-01', 'app-01'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'backup : Install restic' }, { name: 'backup : Deploy repository credentials' },
            { name: 'backup : Initialise repository' }, { name: 'backup : Schedule nightly snapshot 02:00' }, { name: 'backup : Verify last snapshot', retries: true },
        ],
        handlers: [],
    },
    {
        name: 'hardening', hosts: ['web-01', 'web-02', 'web-03', 'app-01', 'app-02', 'db-01', 'db-02', 'cache-01', 'mon-01'],
        tasks: [
            { name: 'Gathering Facts' }, { name: 'cis : Disable unused filesystems', items: ['cramfs', 'squashfs', 'udf'] }, { name: 'cis : Ensure auditd installed' },
            { name: 'cis : Configure audit rules', handler: 'restart auditd' }, { name: 'cis : Set password policy' }, { name: 'cis : Disable root login' },
            { name: 'cis : Configure ufw default deny', handler: 'reload ufw' }, { name: 'cis : Enable AppArmor profiles' }, { name: 'cis : Remove telnet and rsh', items: ['telnet', 'rsh-client'] },
            { name: 'cis : Set login banner' },
        ],
        handlers: ['restart auditd', 'reload ufw'],
    },
];

const shortHash = (n = 7) => Array.from({ length: n }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
const stars = (text: string, cols: number) => '*'.repeat(Math.max(4, cols - text.length - 1));

/* Fixed at 80 cols so ****-padding matches a real ansible run regardless of the box's actual width. */
const COLS = 80;

/* Runs the exact ansible-playbook state machine from scenesPlatform.ts's canvas Scene, but appends
   into React state instead of drawing — canvas pixels can never be text-selected, so this one card
   renders real DOM text instead. */
export default function AnsiblePlaybookText() {
    const palette = useServicePalette();
    const [lines, setLines] = useState<TermLine[]>([]);
    const [title, setTitle] = useState('ansible-playbook site.yml -i inventories/prod --diff · run 1');
    const bodyRef = useRef<HTMLPreElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const state = {
            lines: [] as TermLine[],
            play: 0, task: 0, host: 0, item: 0, handler: 0, stage: 0,
            next: 0, runs: 0, retryLeft: 3,
            tally: new Map<string, [number, number, number, number]>(),
            changedHandlers: new Set<string>(),
        };

        const pushLine = (line: TermLine, cap = 300) => {
            state.lines.push(line);
            while (state.lines.length > cap) state.lines.shift();
        };

        const result = (h: string, forceOk: boolean) => {
            const counts = state.tally.get(h) ?? [0, 0, 0, 0];
            const roll = Math.random();
            let kind: 0 | 1 | 2 | 3;
            if (forceOk || roll < 0.55) kind = 0;
            else if (roll < 0.9) kind = 1;
            else if (roll < 0.97) kind = 3;
            else kind = 2;
            counts[kind]++;
            state.tally.set(h, counts);
            return kind;
        };

        let raf = 0;
        let start = 0;
        let visible = true;

        const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const time = (timestamp - start) / 1000;

            if (time > state.next) {
                const current = SITE[state.play];
                if (state.stage === 0) {
                    if (state.play === 0) {
                        state.lines.length = 0;
                        pushLine([[`ansible-playbook [core 2.17.4] · ${new Date(2026, 8, 15, 2, 0 + state.runs).toISOString().slice(0, 16).replace('T', ' ')}`, DIM]]);
                    }
                    pushLine([['', FG]]);
                    pushLine([[`PLAY [${current.name}] `, BRIGHT], [stars(`PLAY [${current.name}] `, COLS), DIM]]);
                    current.hosts.forEach((h) => state.tally.set(h, state.tally.get(h) ?? [0, 0, 0, 0]));
                    state.changedHandlers.clear();
                    state.task = 0;
                    state.host = 0;
                    state.item = 0;
                    state.stage = 1;
                    state.next = time + 0.35;
                } else if (state.stage === 1) {
                    const tk = current.tasks[state.task];
                    if (state.host === 0 && state.item === 0) {
                        pushLine([['', FG]]);
                        pushLine([[`TASK [${tk.name}] `, BRIGHT], [stars(`TASK [${tk.name}] `, COLS), DIM]]);
                        if (tk.include) pushLine([[`included: /srv/ansible/${tk.include} for ${current.hosts.join(', ')}`, 3]]);
                        state.retryLeft = 3;
                    }
                    const h = current.hosts[state.host];
                    if (tk.retries && state.retryLeft > 0 && Math.random() < 0.55) {
                        pushLine([[`FAILED - RETRYING: [${h}]: ${tk.name.split(' : ')[1] ?? tk.name} (${state.retryLeft} retries left).`, 1]]);
                        state.retryLeft--;
                        state.next = time + 0.35;
                    } else {
                        const kind = result(h, tk.name === 'Gathering Facts');
                        const label = ['ok', 'changed', 'fatal', 'skipping'][kind];
                        const tint = [3, 0, 1, DIM][kind];
                        const suffix = tk.items ? ` => (item=${tk.items[state.item]})` : '';
                        if (kind === 2) {
                            pushLine([[`fatal: [${h}]: FAILED! => `, 1], [`{"changed": false, "msg": "non-zero return code", "rc": 1}`, DIM]]);
                            pushLine([['...ignoring', DIM]]);
                        } else {
                            pushLine([[`${label}: [${h}]${suffix}`, tint]]);
                            if (kind === 1 && tk.diff && state.item === 0) {
                                pushLine([[`--- before: ${tk.name.includes('sshd') ? '/etc/ssh/sshd_config' : tk.name.includes('postgresql.conf') ? '/etc/postgresql/16/main/postgresql.conf' : '/etc/app.env'}`, DIM]]);
                                pushLine([[`+++ after: /home/deploy/.ansible/tmp/${shortHash(8)}`, DIM]]);
                                pushLine([[tk.diff[0], 1]]);
                                pushLine([[tk.diff[1], 0]]);
                            }
                            if (kind === 1 && tk.handler) state.changedHandlers.add(tk.handler);
                        }
                        if (tk.items && state.item < tk.items.length - 1) state.item++;
                        else {
                            state.item = 0;
                            state.host++;
                        }
                        state.next = time + (0.1 + Math.random() * 0.12);
                        if (state.host >= current.hosts.length) {
                            state.host = 0;
                            state.task++;
                            state.next = time + 0.4;
                            if (state.task >= current.tasks.length) {
                                state.stage = state.changedHandlers.size ? 2 : 3;
                                state.handler = 0;
                            }
                        }
                    }
                } else if (state.stage === 2) {
                    const list = current.handlers.filter((hd) => state.changedHandlers.has(hd));
                    if (state.handler < list.length) {
                        pushLine([['', FG]]);
                        pushLine([[`RUNNING HANDLER [${list[state.handler]}] `, BRIGHT], [stars(`RUNNING HANDLER [${list[state.handler]}] `, COLS), DIM]]);
                        current.hosts.forEach((h) => {
                            pushLine([[`changed: [${h}]`, 0]]);
                            const counts = state.tally.get(h) ?? [0, 0, 0, 0];
                            counts[1]++;
                            state.tally.set(h, counts);
                        });
                        state.handler++;
                        state.next = time + 0.5;
                    } else state.stage = 3;
                } else if (state.stage === 3) {
                    state.play++;
                    if (state.play >= SITE.length) {
                        pushLine([['', FG]]);
                        pushLine([['PLAY RECAP ', BRIGHT], [stars('PLAY RECAP ', COLS), DIM]]);
                        const allHosts = Array.from(new Set(SITE.flatMap((pl) => pl.hosts))).sort();
                        allHosts.forEach((h) => {
                            const [ok, changed, failed, skipped] = state.tally.get(h) ?? [0, 0, 0, 0];
                            pushLine([[h.padEnd(12), FG], [`: ok=${String(ok).padEnd(4)}`, 3], [`changed=${String(changed).padEnd(4)}`, 0], ['unreachable=0    ', FG], [`failed=${String(failed).padEnd(4)}`, failed ? 1 : FG], [`skipped=${String(skipped).padEnd(4)}`, DIM], ['rescued=0    ignored=' + failed, DIM]]);
                        });
                        pushLine([['', FG]]);
                        pushLine([[`Playbook run took 0 days, 0 hours, ${12 + state.runs} minutes, ${Math.floor(4 + Math.random() * 54)} seconds`, DIM]]);
                        pushLine([['', FG]]);
                        pushLine([['$ ', 0], ['ansible-playbook site.yml -i inventories/prod --diff', BRIGHT]]);
                        state.play = 0;
                        state.runs++;
                        state.tally.clear();
                        state.next = time + 2.4;
                    } else state.next = time + 0.5;
                    state.stage = 0;
                }

                setLines(state.lines.slice());
                setTitle(`ansible-playbook site.yml -i inventories/prod --diff · run ${state.runs + 1}`);
            }

            if (visible) raf = window.requestAnimationFrame(step);
        };

        raf = window.requestAnimationFrame(step);

        const el = wrapRef.current;
        const observer = el
            ? new IntersectionObserver(
                  ([entry]) => {
                      visible = entry.isIntersecting;
                      if (visible && !raf) raf = window.requestAnimationFrame(step);
                      else if (!visible && raf) {
                          window.cancelAnimationFrame(raf);
                          raf = 0;
                      }
                  },
                  { rootMargin: '150px' }
              )
            : null;
        if (el && observer) observer.observe(el);

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            observer?.disconnect();
        };
    }, []);

    const [capacity, setCapacity] = useState(20);

    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return;
        const measure = () => {
            const cs = getComputedStyle(body);
            const lineH = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5;
            setCapacity(Math.max(1, Math.floor(body.clientHeight / lineH)));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(body);
        return () => observer.disconnect();
    }, []);

    return (
        <Frame ref={wrapRef} $palette={palette}>
            <ChromeBar $palette={palette}>
                <Dots>
                    <Dot $colour={alpha(pick(palette, 1), 0.85)} />
                    <Dot $colour={alpha(pick(palette, 0), 0.85)} />
                    <Dot $colour={alpha(pick(palette, 3), 0.85)} />
                </Dots>
                <Title $palette={palette}>{title}</Title>
            </ChromeBar>
            <Body ref={bodyRef}>
                {lines.slice(-capacity).map((line, i) => (
                    <Row key={i}>
                        {line.map(([text, tint], j) => (
                            <span key={j} style={{ color: spanColour(palette, tint) }}>
                                {text}
                            </span>
                        ))}
                    </Row>
                ))}
            </Body>
        </Frame>
    );
}
